import { Add, AssessmentRounded, Category, FilterAlt, ViewColumn } from '@mui/icons-material';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Badge, CircularProgress, Stack, Tab, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  addTransportCheckedId,
  setFilterTab,
  setGeofenceSearchValue,
  setHistorySelectedTime,
  setIsHistoryOpen,
  setTaskSearchValue,
  setTransportGeofenceView,
  setTransportSearchValue,
} from 'app/actions/map';
import { FilterButton, InputSearch, Protect } from 'app/components';
import config from 'app/config';
import {
  MapFilterGeofenceDialog,
  MapFilterGeofenceList,
  MapFilterReportDialog,
  MapFilterTaskList,
  MapFilterTransportFilterList,
  MapFilterTransportList,
  MapTaskFilterMenu,
  MapTaskViewMenu,
  MapTransportFilterMenu,
  MapTransportViewMenu,
} from 'app/pages/Map';
import { useAsyncAction } from 'app/services';
import { geofenceGroupLoad, taskLoad, transportLoad } from 'app/store/map';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const MapFilterWindow = () => {
  const dispatch = useDispatch();

  const {
    transportItems,
    transportSearchValue,
    geofenceGroupItems,
    geofenceCheckedId,
    geofenceSearchValue,
    filterTab,
    transportGeofenceView,
    taskItems,
    taskSearchValue,
    isHistoryOpen,
    transportSelectedItem,
  } = useSelector((state) => state.map);
  const session = useSelector((state) => state.session.session);
  const canUseHistoryMode = session?.role_id === config.userRoles.ADMIN || session?.role_id === config.userRoles.SUPERADMIN;
  const transportCheckedItems = useSelector((state) =>
    state.map.transportFilterCheckedType === 'filter' && !isHistoryOpen
      ? state.map.transportProcessingCheckedItems
      : state.map.transportCheckedItems
  );
  const MAX_HISTORY_TRANSPORTS = config.maxHistoryTransports;
  const isHistoryModeDisabled = transportCheckedItems.length > MAX_HISTORY_TRANSPORTS;
  const excessCount = transportCheckedItems.length > MAX_HISTORY_TRANSPORTS ? transportCheckedItems.length - MAX_HISTORY_TRANSPORTS : 0;
  const [anchorTransportColumnEl, setAnchorTransportColumnEl] = useState(null);
  const [anchorTransportFilterEl, setAnchorTransportFilterEl] = useState(null);
  const [anchorTaskColumnEl, setAnchorTaskColumnEl] = useState(null);
  const [anchorTaskFilterEl, setAnchorTaskFilterEl] = useState(null);
  const [geofenceDialogOpen, setGeofenceDialogOpen] = useState(false);
  const [transportReportDialogOpen, setTransportReportDialogOpen] = useState(false);

  const {
    asyncAction: fetchTransportData,
    isLoading: isFetchTransportLoading,
    isSuccessful: isFetchTransportSuccessful,
  } = useAsyncAction(transportLoad);
  const {
    asyncAction: fetchGeofenceData,
    isLoading: isFetchGeofenceLoading,
    isSuccessful: isFetchGeofenceSuccessful,
  } = useAsyncAction(geofenceGroupLoad);
  const { asyncAction: fetchTaskData, isLoading: isFetchTaskLoading, isSuccessful: isFetchTaskSuccessful } = useAsyncAction(taskLoad);

  const handleTransportColumnOpen = (event) => {
    setAnchorTransportColumnEl(event.currentTarget);
  };
  const handleHistoryToggle = () => {
    if (!canUseHistoryMode || isHistoryModeDisabled) return;

    if (isHistoryOpen) {
      dispatch(setHistorySelectedTime(null));
      dispatch(setIsHistoryOpen(false));
    } else {
      if (transportCheckedItems.length || transportSelectedItem) {
        if (transportSelectedItem) {
          dispatch(addTransportCheckedId(transportSelectedItem.id));
        }

        dispatch(setIsHistoryOpen(true));
      }
    }
  };

  const handleTransportColumnClose = () => {
    setAnchorTransportColumnEl(null);
  };

  const handleTransportFilterOpen = (event) => {
    setAnchorTransportFilterEl(event.currentTarget);
  };

  const handleTransportFilterClose = () => {
    setAnchorTransportFilterEl(null);
  };

  const handleTransportReportOpen = () => {
    setTransportReportDialogOpen(true);
  };

  const handleTransportReportClose = () => {
    setTransportReportDialogOpen(false);
  };

  const handleTransportSearchChange = (value) => dispatch(setTransportSearchValue(value));

  const handleTabChange = (event, value) => dispatch(setFilterTab(value));

  const handleGeofenceView = () => dispatch(setTransportGeofenceView(!transportGeofenceView));

  const handleGeofenceDialogOpen = () => {
    setGeofenceDialogOpen(true);
  };

  const handleGeofenceDialogClose = () => {
    setGeofenceDialogOpen(false);
  };

  const handleGeofenceSearchChange = (value) => dispatch(setGeofenceSearchValue(value));

  const handleTaskColumnOpen = (event) => {
    setAnchorTaskColumnEl(event.currentTarget);
  };

  const handleTaskColumnClose = () => {
    setAnchorTaskColumnEl(null);
  };

  const handleTaskFilterOpen = (event) => {
    setAnchorTaskFilterEl(event.currentTarget);
  };

  const handleTaskFilterClose = () => {
    setAnchorTaskFilterEl(null);
  };

  const handleTaskSearchChange = (value) => dispatch(setTaskSearchValue(value));

  useEffect(() => {
    switch (filterTab) {
      case 'transport':
        if (filterTab === 'transport' && !isFetchTransportLoading && !isFetchTransportSuccessful && !transportItems.length) {
          fetchTransportData();
        }
        break;
      case 'geofence':
        if (!isFetchGeofenceLoading && !isFetchGeofenceSuccessful && !geofenceGroupItems.length) {
          fetchGeofenceData();
        }
        break;
      case 'task':
        if (!isFetchTaskLoading && !isFetchTaskSuccessful && !taskItems.length) {
          fetchTaskData();
        }
        break;
    }
  }, [filterTab]);

  return (
    <Stack
      direction='column'
      sx={(theme) => ({
        width: '600px',
        height: '100%',
        zIndex: '1010',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        transition: 'transform 300ms',
        boxShadow: '0px 1px 3px ' + alpha(theme.palette.grey.main, 0.12),
      })}>
      <TabContext value={filterTab}>
        <Stack
          direction='row'
          sx={(theme) => ({
            width: '100%',
            position: 'relative',
            '.MuiTabs-root': {
              width: '100%',
              minHeight: '44px',
            },
            '.MuiTabs-flexContainer': {
              borderColor: theme.palette.divider,
              bgcolor: 'background.default',
              ...(filterTab !== 'transport' && {
                borderColor: 'transparent',
              }),
            },
            '.MuiTab-root': {
              minHeight: '44px',
              fontSize: '12px',
              flex: '1 1 0px',
              '&.Mui-selected': {
                color: 'inherit',
                bgcolor: 'background.paper',
              },
              '&.Mui-selected:not(.Mui-selected)': {
                color: theme.palette.text.secondary,
              },
            },
          })}>
          <TabList variant='fullWidth' onChange={handleTabChange}>
            <Tab value='transport' label='Транспорт' disableRipple />
            <Tab value='geofence' label='Геозоны' disableRipple />
            <Tab value='task' label='Задания' disableRipple />
          </TabList>
        </Stack>
        <Stack
          direction='row'
          sx={(theme) => ({
            flex: '1 1 auto',
            display: 'flex',
            overflow: 'hidden',
            '.MuiTabPanel-root': {
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              padding: theme.spacing(0),
            },
            '.MuiTabPanel-root[hidden]': {
              display: 'none',
            },
          })}>
          <TabPanel value='transport' sx={{ position: 'relative' }}>
            {!isFetchTransportLoading && transportItems.length !== 0 && (
              <React.Fragment>
                <Stack
                  direction='row'
                  alignItems='center'
                  sx={(theme) => ({
                    padding: theme.spacing(1),
                    bgcolor: 'background.paper',
                    '.MuiInputBase-root': {
                      padding: '6px 8px',
                      marginRight: theme.spacing(1),
                      ...(geofenceCheckedId.length !== 0 && {
                        marginLeft: theme.spacing(1),
                      }),
                    },
                    '.MuiButton-root + .MuiButton-root, .MuiButton-root + span': {
                      marginLeft: theme.spacing(1),
                    },
                    '.MuiButton-root.Mui-disabled': {
                      backgroundColor: 'background.default',
                      'svg path': {
                        fill: theme.palette.text.disabled,
                      },
                    },
                  })}>
                  {geofenceCheckedId.length !== 0 && (
                    <FilterButton active={transportGeofenceView} onClick={handleGeofenceView} endIcon={<Category fontSize='small' />} />
                  )}
                  <InputSearch
                    placeholder='Название транспортного средства'
                    value={transportSearchValue}
                    onChange={handleTransportSearchChange}
                  />
                  <FilterButton
                    ref={anchorTransportFilterEl}
                    active={!!anchorTransportFilterEl}
                    onClick={handleTransportFilterOpen}
                    endIcon={<FilterAlt fontSize='small' />}>
                    <Badge
                      color='primary'
                      badgeContent={0}
                      sx={(theme) => ({
                        position: 'absolute',
                        top: '28px',
                        right: '14px',
                        padding: theme.spacing(0, 0.5),
                      })}
                    />
                  </FilterButton>
                  <FilterButton
                    ref={anchorTransportColumnEl}
                    active={!!anchorTransportColumnEl}
                    onClick={handleTransportColumnOpen}
                    endIcon={<ViewColumn fontSize='small' />}
                  />
                  <Protect
                    permissions='report'
                    renderPermitted={() => (
                      <FilterButton
                        active={transportReportDialogOpen}
                        disabled={!transportCheckedItems.length}
                        onClick={handleTransportReportOpen}
                        endIcon={<AssessmentRounded fontSize='small' />}
                      />
                    )}
                    renderForbidden={() => <React.Fragment />}
                  />
                  {canUseHistoryMode && (
                    <Tooltip
                      title={
                        isHistoryModeDisabled
                          ? `Ограничение: можно выбрать не более ${MAX_HISTORY_TRANSPORTS} ТС. Уберите ${excessCount} ${excessCount === 1 ? 'ТС' : excessCount < 5 ? 'ТС' : 'ТС'} из выбранных.`
                          : ''
                      }
                      arrow
                      placement='top'>
                      <span>
                        <FilterButton
                          active={isHistoryOpen}
                          onClick={handleHistoryToggle}
                          disabled={isHistoryModeDisabled}
                          endIcon={<HistoryOutlinedIcon fontSize='small' />}
                        />
                      </span>
                    </Tooltip>
                  )}
                  <MapTransportFilterMenu
                    anchorEl={anchorTransportFilterEl}
                    open={!!anchorTransportFilterEl}
                    onClose={handleTransportFilterClose}
                  />
                  <MapTransportViewMenu
                    anchorEl={anchorTransportColumnEl}
                    open={!!anchorTransportColumnEl}
                    onClose={handleTransportColumnClose}
                  />
                  <MapFilterReportDialog
                    open={transportReportDialogOpen}
                    onClose={handleTransportReportClose}
                    onSuccess={handleTransportReportClose}
                  />
                </Stack>
                <Stack
                  direction='column'
                  alignItems='center'
                  justifyContent='center'
                  sx={{
                    height: '100%',
                    overflow: 'hidden',
                    paddingBottom: '30px',
                  }}>
                  <MapFilterTransportList />
                </Stack>
                <Stack
                  direction='row'
                  alignItems='center'
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: '190px auto',
                    width: '100%',
                    position: 'absolute',
                    bottom: '0',
                    height: '30px',
                    bgcolor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: theme.palette.divider,
                    '.MuiDivider-root': {
                      margin: '2px 6px',
                    },
                  })}>
                  <Stack
                    direction='row'
                    sx={{
                      marginLeft: '12px',
                      whiteSpace: 'nowrap',
                      'span:nth-of-type(2)': {
                        paddingLeft: '4px',
                      },
                    }}>
                    <Typography component='span'>Выделено транспорта</Typography>
                    <Typography component='span'>
                      {transportCheckedItems.length} из {transportItems.length}
                    </Typography>
                  </Stack>
                  <Stack direction='row'>
                    <MapFilterTransportFilterList />
                  </Stack>
                </Stack>
              </React.Fragment>
            )}
            {!isFetchTransportLoading && transportItems.length === 0 && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={(theme) => ({
                  width: '100%',
                  height: '100%',
                  padding: theme.spacing(2),
                })}>
                <Typography align='center' variant='body1' component='span'>
                  Здесь пока ничего нет
                </Typography>
                <Typography align='center' color='text.secondary' variant='caption' component='span'>
                  В этом разделе будет находиться список транспорта
                </Typography>
              </Stack>
            )}
            {isFetchTransportLoading && (
              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={(theme) => ({
                  width: '100%',
                  height: '100%',
                  padding: theme.spacing(2),
                })}>
                <CircularProgress size={24} />
              </Stack>
            )}
          </TabPanel>
          <TabPanel value='geofence'>
            <Protect
              permissions='read'
              renderPermitted={() => (
                <React.Fragment>
                  {!isFetchGeofenceLoading && geofenceGroupItems.length !== 0 && (
                    <React.Fragment>
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={(theme) => ({
                          padding: theme.spacing(1),
                          bgcolor: 'background.paper',
                          '.MuiInputBase-root': {
                            padding: '6px 8px',
                            marginRight: theme.spacing(1),
                          },
                        })}>
                        <InputSearch placeholder='Название геозоны' value={geofenceSearchValue} onChange={handleGeofenceSearchChange} />
                        <FilterButton onClick={handleGeofenceDialogOpen} endIcon={<Add fontSize='small' />} />
                        <MapFilterGeofenceDialog open={geofenceDialogOpen} onClose={handleGeofenceDialogClose} />
                      </Stack>
                      <Stack
                        direction='column'
                        sx={{
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden',
                        }}>
                        <MapFilterGeofenceList />
                      </Stack>
                    </React.Fragment>
                  )}
                  {!isFetchGeofenceLoading && geofenceGroupItems.length === 0 && (
                    <Stack
                      direction='column'
                      alignItems='center'
                      justifyContent='center'
                      sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        padding: theme.spacing(2),
                      })}>
                      <Typography align='center' variant='body1' component='span'>
                        Здесь пока ничего нет
                      </Typography>
                      <Typography align='center' color='text.secondary' variant='caption' component='span'>
                        В этом разделе будет находиться список геозон
                      </Typography>
                    </Stack>
                  )}
                  {isFetchGeofenceLoading && (
                    <Stack
                      direction='column'
                      alignItems='center'
                      justifyContent='center'
                      sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        padding: theme.spacing(2),
                      })}>
                      <CircularProgress size={24} />
                    </Stack>
                  )}
                </React.Fragment>
              )}
              renderForbidden={() => (
                <React.Fragment>
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    sx={(theme) => ({
                      width: '100%',
                      height: '100%',
                      padding: theme.spacing(2),
                    })}>
                    <Typography align='center' variant='body1' component='span'>
                      Доступ ограничен
                    </Typography>
                    <Typography align='center' color='text.secondary' variant='caption' component='span'>
                      Под вашей учетной записью доступ ограничен
                    </Typography>
                  </Stack>
                </React.Fragment>
              )}
            />
          </TabPanel>
          <TabPanel value='task'>
            <Protect
              permissions='read'
              renderPermitted={() => (
                <React.Fragment>
                  {!isFetchTaskLoading && taskItems.length !== 0 && (
                    <React.Fragment>
                      <Stack
                        direction='row'
                        alignItems='center'
                        sx={(theme) => ({
                          padding: theme.spacing(1),
                          bgcolor: 'background.paper',
                          '.MuiInputBase-root': {
                            padding: '6px 8px',
                            marginRight: theme.spacing(1),
                          },
                          '.MuiButton-root + .MuiButton-root': {
                            marginLeft: theme.spacing(1),
                          },
                        })}>
                        <InputSearch
                          placeholder='Регистрационный номер транспортного средства'
                          value={taskSearchValue}
                          onChange={handleTaskSearchChange}
                        />
                        <FilterButton
                          ref={anchorTaskFilterEl}
                          active={!!anchorTaskFilterEl}
                          onClick={handleTaskFilterOpen}
                          endIcon={<FilterAlt fontSize='small' />}>
                          <Badge
                            color='primary'
                            badgeContent={0}
                            sx={(theme) => ({
                              position: 'absolute',
                              top: '28px',
                              right: '14px',
                              padding: theme.spacing(0, 0.5),
                            })}></Badge>
                        </FilterButton>
                        <FilterButton
                          ref={anchorTaskColumnEl}
                          active={!!anchorTaskColumnEl}
                          onClick={handleTaskColumnOpen}
                          endIcon={<ViewColumn fontSize='small' />}
                        />
                        <MapTaskFilterMenu anchorEl={anchorTaskFilterEl} open={!!anchorTaskFilterEl} onClose={handleTaskFilterClose} />
                        <MapTaskViewMenu anchorEl={anchorTaskColumnEl} open={!!anchorTaskColumnEl} onClose={handleTaskColumnClose} />
                      </Stack>
                      <Stack
                        direction='column'
                        justifyContent='center'
                        sx={{
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden',
                        }}>
                        <MapFilterTaskList />
                      </Stack>
                    </React.Fragment>
                  )}
                  {!isFetchTaskLoading && taskItems.length === 0 && (
                    <Stack
                      direction='column'
                      alignItems='center'
                      justifyContent='center'
                      sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        padding: theme.spacing(2),
                      })}>
                      <Typography align='center' variant='body1' component='span'>
                        Здесь пока ничего нет
                      </Typography>
                      <Typography align='center' color='text.secondary' variant='caption' component='span'>
                        В этом разделе будет находиться список заданий
                      </Typography>
                    </Stack>
                  )}
                  {isFetchTaskLoading && (
                    <Stack
                      direction='column'
                      alignItems='center'
                      justifyContent='center'
                      sx={(theme) => ({
                        width: '100%',
                        height: '100%',
                        padding: theme.spacing(2),
                      })}>
                      <CircularProgress size={24} />
                    </Stack>
                  )}
                </React.Fragment>
              )}
              renderForbidden={() => (
                <React.Fragment>
                  <Stack
                    direction='column'
                    alignItems='center'
                    justifyContent='center'
                    sx={(theme) => ({
                      width: '100%',
                      height: '100%',
                      padding: theme.spacing(2),
                    })}>
                    <Typography align='center' variant='body1' component='span'>
                      Доступ ограничен
                    </Typography>
                    <Typography align='center' color='text.secondary' variant='caption' component='span'>
                      Под вашей учетной записью доступ ограничен
                    </Typography>
                  </Stack>
                </React.Fragment>
              )}
            />
          </TabPanel>
        </Stack>
      </TabContext>
    </Stack>
  );
};

export default MapFilterWindow;
